/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Browser log relay (LGTM platform onboarding, PR-4).
 *
 * Browser SPAs cannot reach the OTLP endpoint, so structured log events are
 * batched and POSTed to the backend `/logs/batch`, which re-emits them as
 * `service.name=torqe-frontend` (integration doc §8). PHI/PII is sanitized
 * here in the browser AND again at the backend relay.
 *
 * Decoupled by design: the network sender is injected via `initLogRelay`, so
 * the batching/sanitize logic is unit-testable without MSAL or fetch, and the
 * relay never imports the API client (which would risk a logging recursion).
 */

// Mirrors the backend FrontendLogLevel (features/frontend_logs/schemas.py) so the
// two sides stay in contract. The SPA logger only emits debug/info/warn/error
// today, but the relay accepts warning/fatal too — keep them expressible here so a
// fatal event can't be silently downgraded to info at the type boundary.
export type RelayLevel = "debug" | "info" | "warn" | "warning" | "error" | "fatal";

export interface RelayEvent {
  level: RelayLevel;
  message: string;
  timestamp: string;
  category?: string;
  correlation_id?: string;
  context?: Record<string, unknown>;
}

/** Sends a batch to the backend; returns when delivered (or throws). */
export type BatchSender = (events: RelayEvent[]) => Promise<void>;

// Quantifiers are BOUNDED, not `+`. With an unbounded local part this pattern was quadratic:
// against a long run of local-part characters not followed by "@", the engine matched the run to
// the end, failed to find "@", backtracked one character, and repeated from every start position.
// Measured on plain node: 15KB 117ms, 30KB 399ms, 60KB 1424ms, 120KB 5913ms, 240KB 22.9s --
// doubling the input roughly quadrupled the time.
//
// Because sanitizeString runs inside logRelay.enqueue, that was a main-thread freeze of over a
// second whenever a large message was logged, not merely a slow test.
//
// The limits come from RFC 5321: 320 octets for a whole address, 255 for a domain. They bound the
// work at each start position, which makes the scan linear, while still covering any address that
// could plausibly appear. Redaction output is unchanged -- verified identical across normal
// addresses, multiple addresses, uppercase, subdomains, a bare "@", and local parts of 70 and 300
// characters.
const EMAIL = /[a-zA-Z0-9._%+-]{1,320}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,24}/g;
const SSN = /\b\d{3}[ -]\d{2}[ -]\d{4}\b/g;
// Simple linear phone pattern (no nested quantifiers → ReDoS-safe). The backend
// sanitizer is the authoritative control; this is browser-side defense-in-depth.
const PHONE = /\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/g;
const REDACTED = "[REDACTED]";
const SENSITIVE_KEY = /(email|ssn|patient|dob|member_id|phone|token|secret|password)/i;

/** Recursion guard so a cyclic/pathological context can't hang the logger. */
const MAX_DEPTH = 6;

/** Approximate serialized byte size of an event (for the keepalive size cap). */
function _byteLength(event: RelayEvent): number {
  return new TextEncoder().encode(JSON.stringify(event)).length;
}

/** Redact PHI/PII-shaped substrings from a string. */
export function sanitizeString(value: string): string {
  // Skip the email scan entirely when there is no "@" to find. This is the common case for log
  // messages and turns the worst input (a long string with no "@" at all) from ~1.5s at 60KB into
  // ~0.1ms.
  //
  // The bounded quantifiers above are still required and are not redundant with this: a string
  // containing an "@" *somewhere* can still hold a long run of local-part characters that is not
  // followed by one, and that shape defeats the fast path. Measured at 60KB with a fast path but
  // an unbounded pattern: 1669ms. With both: 39ms.
  const withoutEmails = value.includes("@") ? value.replace(EMAIL, REDACTED) : value;
  return withoutEmails.replace(SSN, REDACTED).replace(PHONE, REDACTED);
}

/** Recursively redact a value: strings by pattern, objects/arrays element-wise. */
function sanitizeValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_DEPTH) return REDACTED;
  if (typeof value === "string") return sanitizeString(value);
  if (Array.isArray(value)) return value.map((v) => sanitizeValue(v, depth + 1));
  if (value !== null && typeof value === "object") {
    return sanitizeRecord(value as Record<string, unknown>, depth + 1);
  }
  return value;
}

function sanitizeRecord(record: Record<string, unknown>, depth: number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(record)) {
    // Sensitive key -> redact wholesale regardless of value type (incl. nested
    // objects), so e.g. { patient: { name, dob } } can't leak.
    out[key] = SENSITIVE_KEY.test(key) ? REDACTED : sanitizeValue(val, depth);
  }
  return out;
}

/**
 * Redact a context object: sensitive keys wholesale, string values by pattern.
 * Recurses into nested objects and arrays so PHI/PII nested under an innocuous
 * key can't bypass the filter.
 */
export function sanitizeContext(context: Record<string, unknown>): Record<string, unknown> {
  return sanitizeRecord(context, 0);
}

class LogRelay {
  private queue: RelayEvent[] = [];
  private queuedBytes = 0;
  private sender: BatchSender | null = null;
  private readonly maxBatch = 25;
  // The sender POSTs with keepalive:true (so beforeunload flushes complete), and
  // browsers cap keepalive bodies at 64KB. Flush once the buffered payload nears
  // that so a large batch can't silently exceed it and drop on navigation.
  private readonly maxBytes = 56 * 1024;

  /** Wire the relay to a real sender. Until called, events are dropped. */
  init(sender: BatchSender): void {
    this.sender = sender;
  }

  /** True once a sender is wired (i.e. relaying is active). */
  get enabled(): boolean {
    return this.sender !== null;
  }

  enqueue(
    level: RelayLevel,
    message: string,
    options: { category?: string; correlationId?: string; context?: Record<string, unknown> } = {}
  ): void {
    if (!this.sender) return;
    const event: RelayEvent = {
      level,
      message: sanitizeString(message),
      timestamp: new Date().toISOString(),
    };
    if (options.category) event.category = options.category;
    if (options.correlationId) event.correlation_id = options.correlationId;
    if (options.context) event.context = sanitizeContext(options.context);
    this.queue.push(event);
    this.queuedBytes += _byteLength(event);
    if (this.queue.length >= this.maxBatch || this.queuedBytes >= this.maxBytes) {
      void this.flush();
    }
  }

  /** Send and clear the buffered events. No-op when empty or unwired. */
  async flush(): Promise<void> {
    if (!this.sender || this.queue.length === 0) return;
    const batch = this.queue;
    this.queue = [];
    this.queuedBytes = 0;
    try {
      await this.sender(batch);
    } catch {
      // Never let logging break the app; drop the batch on failure.
    }
  }
}

export const logRelay = new LogRelay();

/** Wire the relay and start periodic + unload flushing. Call once at startup. */
export function initLogRelay(sender: BatchSender, flushIntervalMs = 5000): void {
  logRelay.init(sender);
  if (typeof window !== "undefined") {
    window.setInterval(() => void logRelay.flush(), flushIntervalMs);
    window.addEventListener("beforeunload", () => void logRelay.flush());
  }
}
